/**
 * Activity Log Component
 * Displays recent activity for a customer
 */

import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { ActivityLogEntry, FEATURE_LABELS, USECASE_LABELS } from '../types';

interface ActivityLogProps {
  activities: ActivityLogEntry[];
}

const BRAND_PRIMARY = '#03e8d3';

export const ActivityLog: React.FC<ActivityLogProps> = ({ activities }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const displayedActivities = showAll ? activities : activities.slice(0, 10);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatFeatureName = (name: string): string => {
    return FEATURE_LABELS[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatUseCaseName = (name: string | null): string => {
    if (!name) return 'N/A';
    return USECASE_LABELS[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDuration = (ms: number | null): string => {
    if (ms === null) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatCost = (cost: number | null): string => {
    if (cost === null) return 'N/A';
    return `$${cost.toFixed(4)}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            {activities.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6">
          {activities.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No activity recorded for this time period
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Time
                      </th>
                      <th className="pb-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Feature
                      </th>
                      <th className="pb-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Use Case
                      </th>
                      <th className="pb-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Status
                      </th>
                      <th className="pb-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Model
                      </th>
                      <th className="pb-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Duration
                      </th>
                      <th className="pb-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Tokens
                      </th>
                      <th className="pb-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayedActivities.map((activity) => (
                      <tr key={activity.id} className="hover:bg-gray-50">
                        <td className="py-3 text-sm text-gray-600">
                          {formatDate(activity.created_at)}
                        </td>
                        <td className="py-3 text-sm font-medium text-gray-900">
                          {formatFeatureName(activity.request_type)}
                        </td>
                        <td className="py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                            {formatUseCaseName(activity.usecase)}
                          </span>
                        </td>
                        <td className="py-3">
                          {activity.status === 'success' ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Success</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600">
                              <XCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">{activity.status}</span>
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-xs text-gray-500 font-mono">
                          {activity.model}
                        </td>
                        <td className="py-3 text-sm text-gray-600 text-right font-mono">
                          {formatDuration(activity.time_taken_ms)}
                        </td>
                        <td className="py-3 text-sm text-gray-600 text-right font-mono">
                          {activity.input_tokens !== null && activity.output_tokens !== null
                            ? `${(activity.input_tokens + activity.output_tokens).toLocaleString()}`
                            : 'N/A'}
                        </td>
                        <td className="py-3 text-sm text-gray-600 text-right font-mono">
                          {formatCost(activity.estimated_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {activities.length > 10 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                    style={{ color: BRAND_PRIMARY }}
                  >
                    {showAll ? 'Show Less' : `Show All ${activities.length} Activities`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
